import { Component, NgZone, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, of, throwError, timeout } from 'rxjs';
import moment from 'moment';
import { DateTime } from 'luxon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TopNavbarComponent } from '../../../app/parkifly-bookings/organizations/components/top-navbar/top-navbar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { NativeDateAdapter } from '@angular/material/core';
import { UpperCasePipe, CommonModule, DatePipe } from '@angular/common';
import { CheckboxComponent } from '../../../app/parkifly-bookings/components/checkbox/checkbox.component';
import { SelfServiceService } from '../../../app/services/self-service.service';
import { BookingConfigService } from '../../../app/parkifly-bookings/services/booking-config.service';
import { CheckoutServices } from '../../../app/parkifly-bookings/services/checkout.service';
import { DynamicScriptLoaderService } from '../../../app/services/dynamicScriptLoader.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ParkingService } from '../../../app/services/parking.service';

class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    return moment(date).format('YYYY/MM/DD');
  }
}

const CUSTOM_DATE_FORMATS = {
  parse: { dateInput: 'YYYY/MM/DD' },
  display: { dateInput: 'YYYY/MM/DD', monthYearLabel: 'YYYY', dateA11yLabel: 'YYYY/MM/DD', monthYearA11yLabel: 'YYYY' }
};

declare var payex: any;
@Component({
  selector: 'app-drive-up-details-form',
  templateUrl: './drive-up-details-form.component.html',
  styleUrl: './drive-up-details-form.component.css',
  imports: [TopNavbarComponent, TranslocoModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatInputModule, MatFormFieldModule, MatCheckboxModule, MatRadioModule, MatSelectModule, MatDatepickerModule, MatNativeDateModule, UpperCasePipe, CommonModule, CheckboxComponent, UpperCasePipe, DatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
  ]
})

export class DriveUpDetailsFormComponent {

  @ViewChild('detailsSection') detailsSection!: ElementRef;
  isLoading = false;
  paymentLoadError = false;
  showbookingDetailsForm = false;
  minEndDate: Date = new Date();
  isEditingRegNo = false;
  timeoutError: boolean;
  showDetailsPage: boolean = false;
  showPaymentOption: boolean = false;
  parkings: any = {};
  parkingTo: string;
  parkingFrom: string;
  orderId: string = null;
  orgId = "";
  showRegInputField: boolean = true;;
  isPaymentCompleted = false;
  payexCreditcardUrl = '';
  payexSwishUrl = '';
  payexUrl = '';
  payexSwishLoaded = false;
  selectedPayment: string | null = null;
  isDiscountCodeWrong: boolean = false;
  fullName: string = '';
  totalPrice: any = false;
  parkingCapacityMultiplier: string | null = null;
  timeOptions: string[] = [];
  driveInBookingForm = {
    licensePlate: '',
    gateId: '',
    vehicleOwner: null,
    paymentRequired: 'false'
  };

  bookingDetailsForm = new FormGroup({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    startTime: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    endTime: new FormControl('', [Validators.required, Validators.pattern(/^(0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)]),
  });

  constructor(public translate: TranslocoService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private selfService: SelfServiceService,
    private parkingService: ParkingService,
    private bookingconfig: BookingConfigService,
    public checkoutService: CheckoutServices,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.bookingconfig.setOrganizationFromUrl();
  }

  ngOnInit(): void {

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state;
    if (state) {
      if (state['orderId']) {
        this.orderId = state['orderId'];
      } else {
        this.driveInBookingForm = {
          licensePlate: state['l'],
          gateId: state['g'],
          vehicleOwner: state['vehicleOwner'] ? JSON.parse(state['vehicleOwner']) : null,
          paymentRequired: state['paymentRequired'],
        };
      }
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['orderId']) {
          this.orderId = params['orderId'];
        } else {
          this.driveInBookingForm = {
            licensePlate: params['l'],
            gateId: params['g'],
            paymentRequired: params['paymentRequired'],
            vehicleOwner: params['vehicleOwner'] ? JSON.parse(params['vehicleOwner']) : null
          };
        }
      });
    }

    // Set current Swedish time for start date and time
    this.setCurrentSwedishTime();

    this.showbookingDetailsForm = true;
    const name = this.driveInBookingForm?.vehicleOwner;
    if (name != null) {
      this.fullName = name.firstName + " " + name.lastName;
    }

    this.bookingDetailsForm.controls.endDate.valueChanges.subscribe(() => {
      this.tryCheckPrice();
      this.cdr.markForCheck();
    });

    this.bookingDetailsForm.controls.endTime.valueChanges.subscribe(() => {
      this.tryCheckPrice();
      this.cdr.markForCheck();
    });

    this.bookingDetailsForm.controls.email.valueChanges.subscribe(() => {
      this.cdr.markForCheck();
    });

  }

  setCurrentSwedishTime(): void {
    // Get current time in Swedish timezone (Europe/Stockholm), respecting daylight savings
    const swedenTime = DateTime.now().setZone('Europe/Stockholm');

    // Set start date to current date
    const currentDate = swedenTime.toJSDate();
    this.bookingDetailsForm.controls['startDate'].setValue(currentDate);

    // Set start time in HH:mm format
    const currentTimeHHmm = swedenTime.toFormat('HH:mm');
    this.bookingDetailsForm.controls['startTime'].setValue(currentTimeHHmm);

    // Disable both controls so they can't be edited
    this.bookingDetailsForm.controls['startDate'].disable();
    this.bookingDetailsForm.controls['startTime'].disable();
  }

  checkPrice() {
    if (this.isLoading) return;
    this.isLoading = true;
    const data = {
      gateName: this.driveInBookingForm.gateId,
      parkingTo: this.combineDateAndTime(this.bookingDetailsForm.get('endDate')?.value, this.bookingDetailsForm.get('endTime')?.value)
    };
    this.selfService.calculateDriveUp(data).then((result: any) => {
      this.parkingCapacityMultiplier = result?.parkingCapacityMultiplier ?? null;
      if (result?.amountIncludeVat) {
        this.totalPrice = result.amountIncludeVat;
      } else {
        this.totalPrice = false;
        const message = this.translate.translate('DRIVE_IN_BOOKING.CLICK_AGAIN');
        this.snackBar.open(message, 'OK', {
          duration: 3000,
          verticalPosition: 'top',
          horizontalPosition: 'center'
        });
      }
    }).catch((error) => {
      console.error('calculateDriveUp failed:', error);
      this.totalPrice = false;
      const message = this.translate.translate('DRIVE_IN_BOOKING.CLICK_AGAIN');
      this.snackBar.open(message, 'OK', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }).finally(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  tryCheckPrice() {
    if (this.canCheckPrice()) {
      this.checkPrice();
    }
  }

  canCheckPrice(): boolean {
    const endDate = this.bookingDetailsForm.controls.endDate.value !== null;
    const endTime = this.bookingDetailsForm.controls.endTime.valid;
    return endDate && endTime;
  }

  createBooking() {
    if (this.isLoading || !this.isContinueButtonEnabled) return;
    this.bookingDetailsForm.markAllAsTouched();
    if (!this.bookingDetailsForm.valid) {
      return;
    }
    this.isLoading = true;
    const bookingData = {
      gateName: this.driveInBookingForm.gateId,
      carRegNumber: this.driveInBookingForm.licensePlate,
      email: this.bookingDetailsForm.get('email')?.value,
      parkingTo: this.combineDateAndTime(this.bookingDetailsForm.get('endDate')?.value, this.bookingDetailsForm.get('endTime')?.value),
      parkingCapacityMultiplier: this.parkingCapacityMultiplier
    };

    this.selfService.createDriveUpRegistration(bookingData).then((result: any) => {
      this.isLoading = false;
      if (result.orderId) {
        this.orderId = result.orderId
        this.getOrder();
      } else {
        const message = this.translate.translate('DRIVE_IN_BOOKING.CLICK_AGAIN');
        this.snackBar.open(message, 'OK', {
          duration: 3000,
          verticalPosition: 'top',
          horizontalPosition: 'center'
        });
      }
      this.showbookingDetailsForm = false;
      this.totalPrice = false;
      this.showPaymentOption = true;
    }).catch(() => {
      this.isLoading = false;
      const message = this.translate.translate('DRIVE_IN_BOOKING.CLICK_AGAIN');
      this.snackBar.open(message, 'OK', {
        duration: 3000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }).finally(() => {
      this.isLoading = false;
    });
  }

  getOrder() {
    this.paymentLoadError = false;
    this.checkoutService.getOrder(this.orderId).subscribe({
      next: (order) => {
        const paymentReference = order?.paymentReference && JSON.parse(order?.paymentReference);
        if (paymentReference != null) {
          this.loadScripts(paymentReference);
        } else {
          const message = this.translate.translate('DRIVE_IN_BOOKING.SOMETHING_WRONG');
          this.snackBar.open(message, 'OK', {
            duration: 3000,
            verticalPosition: 'top',
            horizontalPosition: 'center'
          });
        }
      },
      error: () => {
        const message = this.translate.translate('DRIVE_IN_BOOKING.SOMETHING_WRONG');
        this.snackBar.open(message, 'OK', {
          duration: 3000,
          verticalPosition: 'top',
          horizontalPosition: 'center'
        });
      }
    });
  }

  loadScripts(paymentReference: any) {
    if (this.driveInBookingForm.paymentRequired == 'true') {
      const driveUpData = {
        "orderId": this.orderId,
        "gateId": this.driveInBookingForm.gateId
      };
      localStorage.setItem('driveUpData', JSON.stringify(driveUpData));
    }
    const cardModal = document.getElementById('swedbank-pay-seamless-view-page');
    if (cardModal) {
      cardModal.innerHTML = ''; cardModal
    }
    const viewCheckoutOp = paymentReference.operations.find(
      (op) => op.rel === 'view-checkout'
    );
    this.payexUrl = viewCheckoutOp?.href;
    this.dynamicScriptLoaderService.addScript(this.payexUrl);

    this.dynamicScriptLoaderService
      .load(this.payexUrl)
      .then(() => {
        if (typeof payex !== 'undefined') {
          payex.hostedView
            .checkout({
              container: 'swedbank-pay-seamless-view-page',
              onPaymentCompleted: (event: any) => {
                this.ngZone.run(() => {
                  this.isPaymentCompleted = true;
                });
              },
              onPaymentCancelled: (event: any) => {
                this.ngZone.run(() => {
                  this.selectedPayment = null;
                });
              }
            })
            .open();
        } else {
          this.paymentLoadError = true;
        }
      })
      .catch(error => console.error(error));
  }

  resetBookingDetailsForm(): void {
    this.bookingDetailsForm.reset({
      startDate: null,
      startTime: '',
      email: '',
      endDate: null,
      endTime: ''
    });

    this.totalPrice = false;
    this.paymentLoadError = false;
  }

  combineDateAndTime(date: any, time: any): string {
    if (!date) return '';
    let d: Date;
    if (date && typeof date.toDate === 'function') {
      d = date.toDate();
    } else if (date && typeof date.toJSDate === 'function') {
      d = date.toJSDate();
    } else {
      d = new Date(date);
    }

    if (isNaN(d.getTime())) {
      console.warn("Invalid date inside combineDateAndTime:", date);
      return '';
    }

    if (!time) {
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0') + 'T' +
        String(d.getHours()).padStart(2, '0') + ':' +
        String(d.getMinutes()).padStart(2, '0');
    }
    const timeParts = time.split(":");
    const combinedDate = new Date(d.getTime());
    combinedDate.setHours(parseInt(timeParts[0] || '0', 10));
    combinedDate.setMinutes(parseInt(timeParts[1] || '0', 10));
    combinedDate.setSeconds(0);
    combinedDate.setMilliseconds(0);
    const result = combinedDate.getFullYear() + '-' +
      String(combinedDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(combinedDate.getDate()).padStart(2, '0') + 'T' +
      String(combinedDate.getHours()).padStart(2, '0') + ':' +
      String(combinedDate.getMinutes()).padStart(2, '0');
    return result;
  }

  onEndTimeInput(event: any): void {
    let input = event.target.value;
    let digits = input.replace(/\D/g, '');
    if (digits.length > 0) {
      const firstDigit = parseInt(digits[0], 10);
      if (firstDigit > 2) {
        digits = '0' + digits;
      }
    }
    if (digits.length > 4) {
      digits = digits.slice(0, 4);
    }
    let formatted = digits;
    if (digits.length > 2) {
      formatted = digits.slice(0, 2) + ':' + digits.slice(2);
    }
    event.target.value = formatted;
    this.bookingDetailsForm.controls['endTime'].setValue(formatted);
  }

  get isContinueButtonEnabled(): boolean {
    const endDate = this.bookingDetailsForm.controls.endDate.valid;
    const endTime = this.bookingDetailsForm.controls.endTime.valid;
    const email = this.bookingDetailsForm.controls.email.valid;
    return endDate && endTime && email && this.totalPrice !== null && this.totalPrice !== false;
  }

}
